import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Brain, MessageCircle, TrendingUp, AlertTriangle, Send, Sparkles, RotateCcw } from 'lucide-react';
import api from '../../services/api';

const AIInsightsPanel = ({ projects, departmentStats, projectSummary, filters, selectedDepartment }) => {
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const chatContainerRef = useRef(null);
  
  // Default suggested questions
  const defaultQuestions = [
    "Which projects are delayed?",
    "Show budget analysis",
    "Which department has most projects?",
    "What's our completion rate?",
    "Which projects need immediate attention?",
    "Show PWD department performance",
    "Analysis of pending work orders"
  ];

  // Auto-scroll to latest message
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Auto-scroll when chat history changes
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Load AI insights when projects or filters change
  useEffect(() => {
    const loadInsights = async () => {
      if (projects.length === 0) {
        setInsights([]);
        setInsightsLoading(false);
        return;
      }

      setInsightsLoading(true);
      try {
        const aiInsights = await api.ai.getInsights(filters);
        setInsights(aiInsights || []);
      } catch (error) {
        console.error('Failed to load AI insights:', error);
        // Fallback to generated insights
        setInsights(generateFallbackInsights());
      } finally {
        setInsightsLoading(false);
      }
    };

    loadInsights();
  }, [projects, filters]);

  // Generate fallback insights if API fails
  const generateFallbackInsights = () => {
    const totalProjects = projects.length;
    const completedProjects = projects.filter(p => p.status === 'Completed').length;
    const delayedProjects = projects.filter(p => p.status === 'Delayed').length;
    const totalBudget = departmentStats.reduce((sum, dept) => sum + dept.totalAmount, 0);
    
    return [
      {
        type: 'success',
        title: 'Budget Utilization',
        insight: `Water Supply department has highest allocation (₹${(departmentStats.find(d => d.department === 'Water Supply')?.totalAmount / 10000000 || 0).toFixed(1)}Cr) indicating priority on water infrastructure.`,
        action: 'Monitor progress of AMRUT scheme projects'
      },
      {
        type: 'warning', 
        title: 'Project Delays',
        insight: `${delayedProjects} projects are delayed. PWD projects show attention needed for timely completion.`,
        action: 'Review resource allocation for delayed projects'
      },
      {
        type: 'info',
        title: 'Performance Trend',
        insight: `${((completedProjects/totalProjects)*100 || 0).toFixed(1)}% project completion rate. Strong performance across departments.`,
        action: 'Maintain current progress standards'
      }
    ];
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    setIsLoading(true);
    const userMessage = chatInput;
    setChatInput('');
    
    // Add user message to chat
    setChatHistory(prev => [...prev, { type: 'user', message: userMessage }]);
    
    try {
      // Prepare comprehensive dashboard context for AI
      const dashboardContext = {
        // User's question
        message: userMessage,
        
        // Current filters applied
        filters: filters,
        selectedDepartment: selectedDepartment,
        
        // Project Summary Data (the cards at top)
        projectSummary: {
          totalProjects: projectSummary?.total_projects || 0,
          proposedProjects: projectSummary?.proposed_projects || 0,
          workOrderProjects: projectSummary?.work_order_projects || 0,
          completedProjects: projectSummary?.completed_projects || 0,
          completionPercentage: projectSummary?.completion_percentage || 0,
          totalAmount: projectSummary?.total_sanctioned_amount || 0,
          proposedAmount: projectSummary?.proposed_amount || 0,
          workOrderAmount: projectSummary?.work_order_amount || 0,
          completedAmount: projectSummary?.completed_amount || 0
        },
        
        // Department Statistics
        departmentStats: departmentStats.map(dept => ({
          department: dept.department,
          totalProjects: dept.total_projects,
          completedProjects: dept.completed_projects,
          inProgressProjects: dept.in_progress_projects,
          totalAmount: dept.total_sanctioned_amount,
          avgProgress: dept.avg_progress
        })),
        
        // Sample of current projects for context
        projectsSample: projects.slice(0, 10).map(project => ({
          name: project.project_name,
          department: project.department,
          status: project.status,
          amount: project.ts_amount,
          fundCategory: project.fund_category,
          progress: project.progress
        })),
        
        // Overall counts for reference
        totalProjectsInList: projects.length,
        financialYear: filters.financialYear
      };

      // Send comprehensive context to AI API
      const response = await api.ai.sendMessage(userMessage, dashboardContext);
      
      setChatHistory(prev => [...prev, { type: 'ai', message: response.response }]);
    } catch (error) {
      console.error('AI chat error:', error);
      
      // Enhanced fallback responses using actual dashboard data
      const totalProjects = projectSummary?.total_projects || projects.length;
      const completedProjects = projectSummary?.completed_projects || 0;
      const completionRate = projectSummary?.completion_percentage || 0;
      const topDepartment = departmentStats.length > 0 ? departmentStats[0].department : 'PWD';
      
      let fallbackResponse;
      
      if (userMessage.toLowerCase().includes('total') || userMessage.toLowerCase().includes('how many')) {
        fallbackResponse = `### Project Summary (${filters.financialYear})

**Total Projects:** ${totalProjects}
**Completed:** ${completedProjects} 
**Completion Rate:** **${completionRate}%**

**Top Department:** ${topDepartment}

**Key Focus:**
• Improve completion timelines
• Optimize resource allocation`;
      } else if (userMessage.toLowerCase().includes('department')) {
        const deptSummary = departmentStats.map(d => `• **${d.department}:** ${d.total_projects} projects`).slice(0, 3).join('\n');
        fallbackResponse = `### Department Analysis

${deptSummary}

**Leading Department:** ${topDepartment}

**Recommendation:** 
• Review balanced distribution
• Optimize resource allocation`;
      } else if (userMessage.toLowerCase().includes('budget') || userMessage.toLowerCase().includes('amount')) {
        const totalAmount = projectSummary?.total_sanctioned_amount || 0;
        fallbackResponse = `### Budget Analysis

**Total Budget:** **₹${(totalAmount/10000000).toFixed(1)} Cr**
**Completion Rate:** ${completionRate}%

**Focus Areas:**
• Monitor fund utilization
• Ensure timely completion
• Review allocation efficiency`;
      } else if (userMessage.toLowerCase().includes('progress') || userMessage.toLowerCase().includes('status')) {
        fallbackResponse = `### Progress Status

**Project Progress:**
• **Completed:** ${completedProjects} projects
• **Total:** ${totalProjects} projects  
• **Rate:** **${completionRate}%**

**Action Items:**
• Accelerate pending approvals
• Focus on Work Order execution`;
      } else {
        fallbackResponse = `### Dashboard Overview (${filters.financialYear})

**Key Metrics:**
• **Total Projects:** ${totalProjects}
• **Completion Rate:** **${completionRate}%**
• **Top Department:** ${topDepartment}

**Recommendations:**
• Improve project timelines
• Focus on resource optimization
• Maintain quality standards`;
      }
      
      setChatHistory(prev => [...prev, { type: 'ai', message: fallbackResponse }]);
    }
    
    setIsLoading(false);
  };

  // Handle suggested question clicks - directly send the query
  const handleSuggestedQuestionClick = async (question) => {
    setChatInput(question);
    
    // Automatically send the question
    setIsLoading(true);
    
    // Add user message to chat
    setChatHistory(prev => [...prev, { type: 'user', message: question }]);
    
    try {
      // Prepare comprehensive dashboard context for AI
      const dashboardContext = {
        // User's question
        message: question,
        
        // Current filters applied
        filters: filters,
        selectedDepartment: selectedDepartment,
        
        // Project Summary Data (the cards at top)
        projectSummary: {
          totalProjects: projectSummary?.total_projects || 0,
          proposedProjects: projectSummary?.proposed_projects || 0,
          workOrderProjects: projectSummary?.work_order_projects || 0,
          completedProjects: projectSummary?.completed_projects || 0,
          completionPercentage: projectSummary?.completion_percentage || 0,
          totalAmount: projectSummary?.total_sanctioned_amount || 0,
          proposedAmount: projectSummary?.proposed_amount || 0,
          workOrderAmount: projectSummary?.work_order_amount || 0,
          completedAmount: projectSummary?.completed_amount || 0
        },
        
        // Department Statistics
        departmentStats: departmentStats.map(dept => ({
          department: dept.department,
          totalProjects: dept.total_projects,
          completedProjects: dept.completed_projects,
          inProgressProjects: dept.in_progress_projects,
          totalAmount: dept.total_sanctioned_amount,
          avgProgress: dept.avg_progress
        })),
        
        // Sample of current projects for context
        projectsSample: projects.slice(0, 10).map(project => ({
          name: project.project_name,
          department: project.department,
          status: project.status,
          amount: project.ts_amount,
          fundCategory: project.fund_category,
          progress: project.progress
        })),
        
        // Overall counts for reference
        totalProjectsInList: projects.length,
        financialYear: filters.financialYear
      };

      // Send comprehensive context to AI API
      const response = await api.ai.sendMessage(question, dashboardContext);
      
      setChatHistory(prev => [...prev, { type: 'ai', message: response.response }]);
    } catch (error) {
      console.error('AI chat error:', error);
      
      // Enhanced fallback responses using actual dashboard data
      const totalProjects = projectSummary?.total_projects || projects.length;
      const completedProjects = projectSummary?.completed_projects || 0;
      const completionRate = projectSummary?.completion_percentage || 0;
      const topDepartment = departmentStats.length > 0 ? departmentStats[0].department : 'PWD';
      
      let fallbackResponse;
      
      if (question.toLowerCase().includes('total') || question.toLowerCase().includes('how many')) {
        fallbackResponse = `### Project Summary (${filters.financialYear})

**Total Projects:** ${totalProjects}
**Completed:** ${completedProjects} 
**Completion Rate:** **${completionRate}%**

**Top Department:** ${topDepartment}

**Key Focus:**
• Improve completion timelines
• Optimize resource allocation`;
      } else if (question.toLowerCase().includes('department')) {
        const deptSummary = departmentStats.map(d => `• **${d.department}:** ${d.total_projects} projects`).slice(0, 3).join('\n');
        fallbackResponse = `### Department Analysis

${deptSummary}

**Leading Department:** ${topDepartment}

**Recommendation:** 
• Review balanced distribution
• Optimize resource allocation`;
      } else if (question.toLowerCase().includes('budget') || question.toLowerCase().includes('amount')) {
        const totalAmount = projectSummary?.total_sanctioned_amount || 0;
        fallbackResponse = `### Budget Analysis

**Total Budget:** **₹${(totalAmount/10000000).toFixed(1)} Cr**
**Completion Rate:** ${completionRate}%

**Focus Areas:**
• Monitor fund utilization
• Ensure timely completion
• Review allocation efficiency`;
      } else if (question.toLowerCase().includes('progress') || question.toLowerCase().includes('status')) {
        fallbackResponse = `### Progress Status

**Project Progress:**
• **Completed:** ${completedProjects} projects
• **Total:** ${totalProjects} projects  
• **Rate:** **${completionRate}%**

**Action Items:**
• Accelerate pending approvals
• Focus on Work Order execution`;
      } else {
        fallbackResponse = `### Dashboard Overview (${filters.financialYear})

**Key Metrics:**
• **Total Projects:** ${totalProjects}
• **Completion Rate:** **${completionRate}%**
• **Top Department:** ${topDepartment}

**Recommendations:**
• Improve project timelines
• Focus on resource optimization
• Maintain quality standards`;
      }
      
      setChatHistory(prev => [...prev, { type: 'ai', message: fallbackResponse }]);
    }
    
    setChatInput(''); // Clear the input after sending
    setIsLoading(false);
  };

  // Handle chat reset
  const handleChatReset = () => {
    setChatHistory([]);
    setChatInput('');
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'success': return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      case 'info': return <Sparkles className="h-5 w-5 text-blue-600" />;
      default: return <Brain className="h-5 w-5 text-teal-600" />;
    }
  };

  // Format AI response with markdown-like styling
  const formatAIResponse = (message) => {
    let formatted = message;
    
    // Convert markdown formatting to HTML
    // Headers (### Header)
    formatted = formatted.replace(/### (.*?)(?=\n|$)/g, '<h3 style="font-weight: 700; font-size: 1.1em; color: #1e293b; margin-bottom: 8px; margin-top: 12px;">$1</h3>');
    
    // Bold text (**text**)
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 700; color: #0f172a;">$1</strong>');
    
    // Bullet points (• text)
    formatted = formatted.replace(/^• (.*?)(?=\n|$)/gm, '<div style="margin-left: 16px; margin-bottom: 4px;"><span style="color: #059669; font-weight: bold;">•</span> $1</div>');
    
    // Line breaks
    formatted = formatted.replace(/\n/g, '<br/>');
    
    return <div dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'success': return 'bg-green-50 border-green-200';
      case 'warning': return 'bg-orange-50 border-orange-200'; 
      case 'info': return 'bg-blue-50 border-blue-200';
      default: return 'bg-teal-50 border-teal-200';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* AI Insights */}
      <Card className="p-6 bg-gradient-to-br from-white to-purple-50 border-0 shadow-lg">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">AI Insights & Analytics</h3>
            <p className="text-sm text-slate-600">Real-time analysis and recommendations</p>
          </div>
        </div>

        {insightsLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-slate-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {insights.map((insight, index) => (
              <div key={index} className={`p-4 rounded-xl border ${getInsightColor(insight.type)}`}>
                <div className="flex items-start space-x-3">
                  {getInsightIcon(insight.type)}
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800 mb-1">{insight.title}</h4>
                    <p className="text-sm text-slate-600 mb-2">{insight.insight}</p>
                    <Badge variant="outline" className="text-xs">
                      {insight.action}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-purple-200">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>Predictive Analysis</span>
            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
              AI Powered
            </Badge>
          </div>
        </div>
      </Card>

      {/* AI Chatbot */}
      <Card className="p-6 bg-gradient-to-br from-white to-teal-50 border-0 shadow-lg">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl">
            <MessageCircle className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Ask AI Assistant</h3>
            <p className="text-sm text-slate-600">Get instant answers about your projects</p>
          </div>
        </div>

        <div 
          ref={chatContainerRef}
          className="space-y-4 max-h-80 overflow-y-auto mb-4 scroll-smooth"
        >
          {chatHistory.length === 0 && (
            <div className="text-center py-4">
              <div className="text-slate-400 mb-2">
                <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
              </div>
              <p className="text-slate-500 text-sm mb-4">
                Ask me anything about your projects, budgets, or performance metrics!
              </p>
              <div className="grid grid-cols-2 gap-2 max-w-md mx-auto">
                {defaultQuestions.slice(0, 7).map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestedQuestionClick(question)}
                    className="bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 
                             border border-teal-200 hover:border-teal-300 rounded-full px-3 py-2 
                             text-xs text-teal-700 hover:text-teal-800 font-medium 
                             transition-all duration-200 hover:shadow-md transform hover:scale-105 
                             text-left leading-tight"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {chatHistory.map((chat, index) => (
            <div key={index} className={`flex ${chat.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-3 rounded-lg ${
                chat.type === 'user' 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-slate-100 text-slate-800'
              }`}>
                {chat.type === 'ai' ? (
                  <div className="text-sm">{formatAIResponse(chat.message)}</div>
                ) : (
                  <p className="text-sm">{chat.message}</p>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 p-3 rounded-lg">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat Input and Reset Button */}
        <div className="space-y-2">
          <form onSubmit={handleChatSubmit} className="flex space-x-2">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Ask about projects, budgets, delays..."
              className="flex-1 border-teal-200 focus:border-teal-400"
              disabled={isLoading}
            />
            <Button 
              type="submit" 
              disabled={isLoading || !chatInput.trim()}
              className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
          
          {/* Reset Button */}
          {chatHistory.length > 0 && (
            <div className="flex justify-end">
              <Button
                onClick={handleChatReset}
                variant="outline"
                size="sm"
                className="text-slate-600 border-slate-300 hover:bg-slate-100 text-xs"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset Chat
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AIInsightsPanel;